import { NumberSource, QueryFactory } from '@lmt/shared-types';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import { PatientDataRequestToast } from '@lmt/unicorn-shared/app/components/patients/notifications/PatientDataRequestToast';
import { ReactComponent as ClockIcon } from '@lmt/unicorn-shared/app/icons/info.svg';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import styles from './PatientDataReminder.module.scss';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';

import { PatientDataReminderInfo } from './types';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { getLongMonthDate } from '@lmt/unicorn-shared/app/components/patients/common';

export const PatientDataReminder: React.FC<PatientDataReminderInfo> =
  React.memo(function PatientDataReminder(props) {
    const { number, numberSource, lastUpdatedDate, enabled } = props;
    if (numberSource == NumberSource.Autogenerated || !enabled) {
      return null;
    }
    const i18n = useScopedTranslation('patients.overview.info.reminder');
    const dispatch = useUniDispatch();
    const modals = useModal();
    const requestPatient = useCallback(async () => {
      if (!enabled) {
        return;
      }
      try {
        await ClientFactory.HL7OutgoingMessageClient.sendPatientRequestMessage({
          patientNumber: number,
          subtenantId: props.subtenantId,
        });
        toast(<PatientDataRequestToast />, {
          autoClose: false,
        });
      } catch (error) {
        await modals.showError({ text: convertToErrorString(error) });
      }
    }, [dispatch, enabled, number]);

    return (
      <div className={styles.container}>
        <AppTooltip
          arrow={true}
          title={
            <div className={styles.tooltip}>
              <div className={styles.title}>{i18n.t('title')}</div>
              {lastUpdatedDate && (
                <div className={styles.text}>
                  {i18n.t('text', { date: getLongMonthDate(lastUpdatedDate) })}
                </div>
              )}
              <UniLink
                color={ButtonColor.Primary}
                title={i18n.t('button')}
                onClick={requestPatient}
                className={styles.button}
              />
            </div>
          }
        >
          <ClockIcon />
        </AppTooltip>
      </div>
    );
  });
